/**
 * 68.9 — the squad leader sets the squad's own objectives (DIRECTION §0.7, lane D's note; the owner, Oct 10: "bots working
 * together somewhat with a squad leader setting lower level objectives").
 *
 * Within the commander's order (take a point, hold here, go there) the leader's brain picks the squad's **step**, keeps it
 * in its own brain (`ActionBrainState.lead` — 64.8: what a bot knows lives in the state), and the members read it there and
 * follow it, rather than each reading the order alone. Bot-led squads only: when the unit you hold is the leader, you lead
 * (the owner's ruling) and the members follow you as today. Behind `GameConfig.squadLead` (the Lab's row), off by default.
 *
 * **The leader** (decision 139, the owner: "no leader promotion system at the moment, just respawn of that leader"): the
 * squad's designated unit — its first at the battle's start (`MatchSquad.leader`). While it is down the squad has no
 * leader and its members read the commander's order each for themselves, as before 68.9 (so the leader, whose look sets it
 * apart, is the one worth shooting); when it respawns into its squad it leads again.
 *
 * The steps, worked out again a position period after the last (the leader deliberates on its look slot):
 *   - **approach** — to a staging point on the side of the objective the leader chose (out of the known enemy's sight, by
 *     cover, high, not far round, never through the enemy's side), just outside the hold zone; straight in while nothing
 *     is known. The members follow the leader (their slots, in a formation).
 *   - **bound** — in contact (an enemy it knows of within LEAD.contactPeople of the leader or a member), the squad goes on by halves:
 *     one half walks to posts behind cover a bound ahead (68.15's chooseHoldPosts at the bound point, facing the enemy)
 *     while the other holds where it stands and covers; when the movers are on their posts, or LEAD.boundSeconds pass, the
 *     halves swap. The leader's half covers first.
 *   - **hold** — inside the hold zone (68.15's twelve person heights), posts at the objective facing the enemy (inside the
 *     capture circle, for a point).
 *   - **drill** (66.7, coverMoves: the contact drill on the march) — on the way (not yet in the hold zone), contact: an
 *     enemy it knows of inside the rifle's effective range of any member, or a member hit (or, with suppression, pinned).
 *     The squad goes to the nearest cover posts against the contact (chooseCoverPosts round where it stands, dead ground
 *     counting), fights from them, and after LEAD.drillSeconds the leader calls it (drillCall): withdraw by bounds, away
 *     from the contact, as the units' own judgement says (actionJobs' retreat, for the squad); else, under a move or a take
 *     order (the coordinator's ruling: the owner's "bounding from cover to cover while providing cover", the drill its first
 *     beat), bound on — unless outnumbered (more enemies known near the contact than its living members) or under half
 *     health, when it holds the posts and looks again;
 *     with no such order, the jobs' call (engage — hold and look again; take — bound on). No contact: the march goes on.
 *     With the drill, contact is the drill's (the bound comes by its call).
 * Pure; deterministic math only.
 */
import type { CoverPose, GameState, LeadStep, MatchSquad, PlayerState, SquadOrder, Vec3 } from '../types';
import { PERSON_BODY_HEIGHT } from '../constants';
import { vecAdd, vecCross, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { dacos, dcos, dsin } from '../core/dmath';
import { personPointOn, personLineOfSight, personSurfaceRadius } from '../core/fineTerrain';
import { segmentCovered } from '../core/bullets';
import { stepToward } from '../core/tactics';
import type { WalkObstacle } from '../core/surfaceWalk';
import type { PersonSet } from '../core/scaleProfile';
import type { ActionParams } from './botParamsAction';
import { chooseHoldPosts, chooseCoverPosts, postHold, bearingSwing, HOLD_FACE_ARC, HOLD_POST_RADIUS_PEOPLE } from './actionPositions';
import { chooseJob } from './actionJobs';
import { localForce } from '../core/localForce';
import { brainFace } from '../core/brainFace';
import { rifleEffectivePeople } from '../core/support';
import { defaultOn } from '../core/defaultOn';

/** The leader's rules, in person heights and seconds. */
export const LEAD = {
  /** A known enemy within this of the leader or a member: contact — the squad goes on by bounds. */
  contactPeople: 22,
  /** A bound's point stands this far on from the covering half, toward where the squad is going. */
  boundPeople: 8,
  /** The movers' posts stand within this of the bound's point. */
  boundPostPeople: 3,
  /** A bound ends when its movers are on their posts, or after this long. */
  boundSeconds: 6,
  /** The staging point: this far past the hold zone's edge. */
  stagePeople: 2,
  /** On a post, or at the staging point: within this. */
  arrivePeople: 0.6,
  /** The bearings tried round the objective for the approach. */
  bearings: 12,
  /** A side must beat straight in by this much to be worth the walk round. */
  sideMargin: 0.5,
  /** 66.7 (coverMoves) the contact drill: the posts it takes stand within this of where the squad stands. */
  drillReachPeople: 6,
  /** The drill holds this long before the leader calls it (and a hold's call this long before it looks again). */
  drillSeconds: 4,
  /** A member hit within this long: the squad is taking fire. */
  drillHurtSeconds: 2,
  /** A withdrawal falls back this far from the contact's side of the squad. */
  withdrawPeople: 12,
  /** 66.19: an assault's local read round the contact (people) — against it, the drill; else it keeps bounding. */
  assaultReadPeople: 20,
} as const;

/** The orders a leader turns into steps (68.9): take, hold and go-here, at a point or a place. */
export type LeadOrder = Extract<SquadOrder, { kind: 'take' | 'hold' | 'move' }>;
export const isLeadOrder = (o: SquadOrder | null | undefined): o is LeadOrder => !!o && (o.kind === 'take' || o.kind === 'hold' || o.kind === 'move');

/** The key of the order a step serves: its kind and its point, or its place. */
export function leadOrderKey(order: LeadOrder, place: { planetIndex: number; surfaceDir: Vec3 }): string {
  const kind = order.kind === 'hold' && order.ambush ? 'hold!ambush' : order.kind;   // (66.11: an ambush is its own step, armed afresh)
  if (order.pointId !== undefined) return `${kind}|p${order.pointId}`;
  const d = vecNormalize(place.surfaceDir);
  return `${kind}|${place.planetIndex}:${d.x.toFixed(6)},${d.y.toFixed(6)},${d.z.toFixed(6)}`;
}

/** The squad's leader now (decision 139): its designated unit while that is alive and in the squad, else none. */
export function squadLeadOf(state: Pick<GameState, 'players'>, squad: Pick<MatchSquad, 'unitIds' | 'leader'>): PlayerState | null {
  const id = squad.leader ?? squad.unitIds[0];
  if (id === undefined || !squad.unitIds.includes(id)) return null;
  const u = state.players.find(p => p.id === id);
  return u && u.alive ? u : null;
}

/** The hold zone's radius (u): 68.15's — within it, the squad takes its posts. */
export function holdZone(ps: PersonSet): number {
  return 2 * HOLD_POST_RADIUS_PEOPLE * ps.hitboxCenter * 2;
}

export interface LeadArgs {
  state: GameState;
  /** The order's key (leadOrderKey). */
  order: string;
  /** Where the order points; `captureRadius` set for a capture point (the posts stand inside it), null for a place. */
  objective: { planetIndex: number; surfaceDir: Vec3; captureRadius: number | null };
  leader: PlayerState;
  /** The squad's living members the bots drive, on the objective's planet, in the squad's order (the leader first). */
  members: readonly PlayerState[];
  /** The enemies the leader knows of on this planet (surface directions, the most confident first). */
  threats: readonly Vec3[];
  /** Where the squad faces with nothing known: on past the objective (a surface direction). */
  ahead: Vec3;
  /** 66.7 (coverMoves): each member's threat its hold post shelters from (a world point: chooseCoverPosts). */
  threatOf?: ReadonlyMap<number, Vec3>;
  /** 66.7 bound-with-cover (coverMoves): the catalogue's dials (botParamsAction `action.bound`); with `threatOf`, a bound's posts are cover posts. */
  bound?: ActionParams['bound'];
  /** 66.7 (coverMoves): the contact drill on the march (see the module note). */
  drill?: boolean;
  /** 66.11: the order is an ambush hold — its hold step carries `ambush` (armed when the hold begins; sprung by the leader's brain). */
  ambush?: boolean;
  ps: PersonSet;
  obstacles: readonly WalkObstacle[];
  /** The leader's step so far (null: none yet). */
  prev: LeadStep | null;
  tick: number;
  /** The tick's length (s). */
  dt: number;
}

/** The squad's step now, from the leader's point of view (see the module note). */
export function leadStep(a: LeadArgs): LeadStep {
  const { state, ps, members, threats, tick } = a;
  const pi = a.objective.planetIndex;
  const planet = state.planets[pi];
  const R = planet.radius;
  const person = PERSON_BODY_HEIGHT * ps.k;
  const O = vecNormalize(a.objective.surfaceDir);
  const arc = (x: Vec3, y: Vec3) => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(x), vecNormalize(y))))) * R;
  const world = (d: Vec3) => vecAdd(planet.position, vecScale(vecNormalize(d), R));
  const prev = a.prev && a.prev.order === a.order && a.prev.planetIndex === pi ? a.prev : null;
  const L = vecNormalize(a.leader.surfaceDir);
  const zone = holdZone(ps);
  const ids = members.map(m => m.id);
  // where the squad faces: the known enemy nearest the objective, else on past it
  const face = threats.length ? threats.reduce((best, t) => (arc(t, O) < arc(best, O) ? t : best)) : vecNormalize(a.ahead);
  const step = (kind: LeadStep['kind'], rest: Partial<LeadStep>): LeadStep => ({ order: a.order, kind, planetIndex: pi, to: O, face, posts: [], moving: [], since: tick, at: tick, staged: true, ...rest });

  // (66.11: an ambush's hold carries it — armed when the hold began, sprung as the leader's brain set it)
  const ambushOf = (): Partial<LeadStep> => a.ambush ? { ambush: prev?.kind === 'hold' && prev.ambush ? { ...prev.ambush } : { armed: tick } } : {};
  // hold: in the zone (a little past it once holding, so the squad does not flicker at the edge)
  const dLead = arc(L, O);
  if (dLead <= zone || (prev?.kind === 'hold' && dLead <= 1.5 * zone)) {
    if (a.threatOf) {
      // 66.7 (coverMoves): a post per member against its own threat (chooseCoverPosts); they stand while the squad is the
      // same and no member's threat has swung past HOLD_FACE_ARC; at a capture point one keeps the circle
      const was = prev?.kind === 'hold' ? prev.threats : undefined;
      const keepSame = !!was && prev!.posts.length === ids.length && ids.every(id => {
        const x = was.find(([w]) => w === id)?.[1], y = a.threatOf!.get(id);
        return !!x && !!y && prev!.posts.some(([p]) => p === id) && bearingSwing(O, vecSub(x, planet.position), vecSub(y, planet.position)) < HOLD_FACE_ARC;
      });
      const posts = keepSame ? prev!.posts : [...chooseCoverPosts({ state, planetIndex: pi, place: O, ps, obstacles: a.obstacles, members, threatOf: a.threatOf,
        ...(a.objective.captureRadius !== null ? { keep: 0.75 * a.objective.captureRadius } : {}),
        ...(prev?.kind === 'hold' ? { current: new Map(prev.posts) } : {}) })];
      // (how each stands at its post: the view and the gun kept — chosen with the posts)
      const holds = keepSame && prev!.stances ? null : posts.map(([id, d]) => [id, postHold(state, pi, d, a.threatOf!.get(id) ?? world(face), ps, a.obstacles)] as const);
      // (lean step 2: a lean post moved to its cover's edge is the post it takes)
      if (holds) holds.forEach(([id, h], i) => { if (h.at) posts[i] = [id, h.at]; });
      const stances = holds ? holds.map(([id, h]) => [id, h.stance] as [number, 'crouch' | 'stand' | 'prone' | 'rise']) : prev!.stances!;
      const poses = holds ? holds.filter(([, h]) => h.pose).map(([id, h]) => [id, h.pose!] as [number, { pose: CoverPose; side?: 1 | -1; step?: number }]) : prev!.poses ?? [];
      return step('hold', { posts, threats: keepSame ? prev!.threats! : [...a.threatOf], stances, poses, since: keepSame ? prev!.since : tick, ...ambushOf() });
    }
    const same = prev?.kind === 'hold' && prev.posts.length === ids.length && prev.posts.every(([id]) => ids.includes(id)) && arc(prev.face, face) < 2 * person;
    const posts = same ? prev!.posts : [...chooseHoldPosts({ state, planetIndex: pi, place: O, facing: world(face), ps, obstacles: a.obstacles, members,
      ...(a.objective.captureRadius !== null ? { reach: 0.75 * a.objective.captureRadius } : {}),
      ...(prev?.kind === 'hold' ? { current: new Map(prev.posts) } : {}) })];   // (66.7 b: each keeps its post)
    return step('hold', { posts, since: same ? prev!.since : tick, ...ambushOf() });
  }

  // the way in: the staging point while it stands (prev.to, not yet staged), else the objective; a side is chosen once
  // something is known (and again if the squad lost its step)
  let to = O, staged = prev?.staged ?? false;
  const sideOpen = !prev || (!prev.staged && arc(prev.to, O) < 1e-6 * R);
  if (prev && !sideOpen) { to = prev.to; staged = prev.staged; }
  if (sideOpen && threats.length) {
    const s = approachSide(a, O, L, zone, person, arc);
    if (s) { to = s; staged = false; } else staged = true;
  }
  if (!staged && arc(to, O) > 1e-6 * R) {
    // reached the staging point (the leader, or a bound's covering half): straight in from here
    if (arc(L, to) < 2 * LEAD.arrivePeople * person) { to = O; staged = true; }
  }

  // bound: in contact, by halves (toward `goal`: the way in, or a withdrawal's fall-back point)
  const bound = (goal: Vec3, drill?: LeadStep['drill']): LeadStep => {
    const alive = (id: number) => members.find(m => m.id === id);
    if (prev?.kind === 'bound') {
      const movers = prev.moving.filter(id => ids.includes(id));
      const postOf = (id: number) => prev.posts.find(([x]) => x === id)?.[1] ?? null;
      const onPosts = movers.every(id => { const p = postOf(id), m = alive(id); return !p || !m || arc(m.surfaceDir, p) < LEAD.arrivePeople * person; });
      const due = (tick - prev.since) * a.dt >= LEAD.boundSeconds;
      if (movers.length && !onPosts && !due) return { ...prev, to: goal, staged, face, at: tick, posts: prev.posts.filter(([id]) => ids.includes(id)), moving: movers, ...(drill ? { drill } : {}) };
    }
    // a new bound: the half that covered moves (the leader's half covers the first)
    const odd = members.filter((_, i) => i % 2 === 1).map(m => m.id), even = members.filter((_, i) => i % 2 === 0).map(m => m.id);
    let moving = prev?.kind === 'bound' ? ids.filter(id => !prev.moving.includes(id)) : odd;
    if (moving.length === 0 || moving.length === ids.length) moving = prev?.kind === 'bound' && prev.moving.some(id => odd.includes(id)) ? even : odd;
    const covering = members.filter(m => !moving.includes(m.id));
    const from = covering.length ? centroid(covering.map(m => m.surfaceDir)) : L;
    let B = stepToward(from, goal, LEAD.boundPeople * person, R);
    // 66.7 bound-with-cover, the offset bound (coverMoves): the bound's point stands bound.offsetPeople to the side of the
    // covering half's line to the threat, on the side the movers are on — the halves leapfrog on parallel tracks and the
    // covering half's fire has a lane past them (straight ahead, the movers stood in it and held its fire)
    if (a.threatOf && a.bound && a.bound.offsetPeople > 0) {
      const n = vecNormalize(from), toThreat = vecSub(vecNormalize(face), vecScale(n, vecDot(vecNormalize(face), n)));
      if (vecLength(toThreat) > 1e-12) {
        const fwd = vecNormalize(toThreat), side = vecNormalize(vecCross(n, fwd));
        const moversAt = centroid(members.filter(m => moving.includes(m.id)).map(m => m.surfaceDir));
        let sgn = vecDot(vecSub(moversAt, n), side) >= 0 ? 1 : -1;
        if (Math.abs(vecDot(vecSub(moversAt, n), side)) * R < 0.25 * person) sgn = moving.includes(members[1]?.id ?? -1) ? 1 : -1;   // (on the line: by the squad's order)
        const b0 = vecNormalize(B), th = (a.bound.offsetPeople * person) / R;
        const across = vecNormalize(vecSub(side, vecScale(b0, vecDot(side, b0))));
        B = vecNormalize(vecAdd(vecScale(b0, dcos(th)), vecScale(across, sgn * dsin(th))));
      }
    }
    const movers = members.filter(m => moving.includes(m.id));
    if (a.threatOf && a.bound) {
      // 66.7 bound-with-cover (coverMoves): the movers' stops are cover posts round the bound's point, each against its own
      // threat as a hold's are; the half that covers keeps the posts it bounded to, or (the first bound) settles into the
      // cover nearest it; both take their posts' stances and poses — the covering half fires from cover (actionBot)
      const threatOf = a.threatOf;
      const moverPosts = [...chooseCoverPosts({ state, planetIndex: pi, place: B, ps, obstacles: a.obstacles, members: movers, threatOf, reach: a.bound.coverReachPeople * person })];
      const had = prev?.kind === 'bound' ? new Map(prev.posts) : null;
      const settled = covering.filter(m => had?.has(m.id));
      const settle = covering.filter(m => !had?.has(m.id));
      const coverPosts: Array<[number, Vec3]> = [
        ...settled.map(m => [m.id, had!.get(m.id)!] as [number, Vec3]),
        ...(settle.length ? [...chooseCoverPosts({ state, planetIndex: pi, place: centroid(settle.map(m => m.surfaceDir)), ps, obstacles: a.obstacles, members: settle, threatOf, reach: a.bound.settlePeople * person })] : []),
      ];
      const posts = [...moverPosts, ...coverPosts];
      const holds = posts.map(([id, d]) => [id, postHold(state, pi, d, threatOf.get(id) ?? world(face), ps, a.obstacles)] as const);
      holds.forEach(([id, h], i) => { if (h.at) posts[i] = [id, h.at]; });   // (lean step 2: a lean post at its cover's edge)
      const stances = holds.map(([id, h]) => [id, h.stance] as [number, 'crouch' | 'stand' | 'prone' | 'rise']);
      const poses = holds.filter(([, h]) => h.pose).map(([id, h]) => [id, h.pose!] as [number, { pose: CoverPose; side?: 1 | -1; step?: number }]);
      return step('bound', { to: goal, staged, posts, moving: movers.map(m => m.id), threats: [...threatOf], stances, poses, ...(drill ? { drill } : {}) });
    }
    const posts = [...chooseHoldPosts({ state, planetIndex: pi, place: B, facing: world(face), ps, obstacles: a.obstacles, members: movers, reach: LEAD.boundPostPeople * person })];
    return step('bound', { to: goal, staged, posts, moving: movers.map(m => m.id), ...(drill ? { drill } : {}) });
  };

  // 66.7 (coverMoves): the contact drill on the march — contact takes the nearest cover first, and the leader's call
  if (a.drill) {
    const reach = rifleEffectivePeople() * person;
    const near = threats.filter(t => members.some(m => arc(t, m.surfaceDir) <= reach));
    const hit = members.some(m => {
      const h = brainFace(state, m.id)?.hurtAt;   // (§5.16 step 2: a brain read through its public face)
      return (h !== undefined && (tick - h) * a.dt < LEAD.drillHurtSeconds) || (defaultOn(state.config, 'suppression') && (m.suppressed ?? 0) > 0);
    });
    const was = prev?.drill;
    if (near.length === 0 && !hit) return step('approach', { to, staged, since: prev?.kind === 'approach' ? prev.since : tick });   // (no contact: the march goes on)
    const mid = centroid(members.map(m => m.surfaceDir));
    const c = near.length ? world(near.reduce((x, y) => (arc(x, mid) <= arc(y, mid) ? x : y))) : was?.contact ?? world(face);
    const drillPosts = (): LeadStep => {
      const threatOf = new Map(members.map(m => [m.id, c] as const));
      const posts = [...chooseCoverPosts({ state, planetIndex: pi, place: mid, ps, obstacles: a.obstacles, members, threatOf, reach: LEAD.drillReachPeople * person })];
      const holds = posts.map(([id, d]) => [id, postHold(state, pi, d, c, ps, a.obstacles)] as const);
      holds.forEach(([id, h], i) => { if (h.at) posts[i] = [id, h.at]; });
      const stances = holds.map(([id, h]) => [id, h.stance] as [number, 'crouch' | 'stand' | 'prone' | 'rise']);
      const poses = holds.filter(([, h]) => h.pose).map(([id, h]) => [id, h.pose!] as [number, { pose: CoverPose; side?: 1 | -1; step?: number }]);
      return step('drill', { to, staged, posts, threats: posts.map(([id]) => [id, c] as [number, Vec3]), stances, poses, drill: { contact: c, began: tick } });
    };
    // 66.19 (the owner: fix the attack, then cover on): an assault (a take, a play's bound) keeps bounding under fire — the
    // covering half fires on the group it knows, the rest move to the next cover toward the point — and goes to ground only
    // when the local read round the contact is against it (its living and ours there fewer than their believed number)
    const orderKind = a.order.split('|')[0];
    if (orderKind === 'take' || orderKind === 'bound') {
      const lf = localForce(state, members[0].teamIndex, { planetIndex: pi, surfaceDir: vecNormalize(vecSub(c, planet.position)) }, LEAD.assaultReadPeople, tick);
      // (their number: the side's beliefs there, or what the leader itself knows round the contact, whichever is more)
      const known = threats.filter(t => arc(t, c) <= LEAD.assaultReadPeople * person).length;
      const against = (members.length + lf.friends) < Math.max(lf.enemies, known);
      if (!against && was?.call !== 'withdraw') return bound(to, { ...(was ?? { began: tick }), contact: c, call: 'bound', calledAt: was?.calledAt ?? tick });
    }
    // the call under way: a bound on, or a withdrawal (to its fall-back point; there, a fresh drill)
    if (prev?.kind === 'bound' && was?.call === 'bound') return bound(to, { ...was, contact: c });
    if (prev?.kind === 'bound' && was?.call === 'withdraw') return arc(mid, prev.to) < 2 * LEAD.arrivePeople * person ? drillPosts() : bound(prev.to, { ...was, contact: c });
    if (prev?.kind !== 'drill' || !was) return drillPosts();
    // the drill: the posts stand until the leader has looked (and a hold's call until it looks again)
    if ((tick - (was.calledAt ?? was.began)) * a.dt < LEAD.drillSeconds) return { ...prev, at: tick, drill: { ...was, contact: c } };
    const kind = a.order.split('|')[0];
    const call = drillCall(members, near.length, arc(L, O) / ps.k, kind === 'move' || kind === 'take');
    if (call === 'hold') return { ...prev, at: tick, drill: { ...was, contact: c, call, calledAt: tick } };
    if (call === 'bound') return bound(to, { ...was, contact: c, call, calledAt: tick });
    // withdraw: back from the contact, along the line from it through the squad
    const away = vecSub(mid, vecScale(vecNormalize(vecSub(c, planet.position)), 1));
    const back = stepToward(mid, vecNormalize(vecAdd(mid, away)), LEAD.withdrawPeople * person, R);
    return bound(back, { ...was, contact: c, call, calledAt: tick });
  }

  // (68.9, lane B's finding with the fire lanes: contact is the squad's — a known enemy within reach of the leader or of
  // any member under the step; measured from the leader alone, a leader back from its respawn 27 people off walked in for
  // 3 s while its members, 20 people off, fought with no step)
  const contact = threats.some(t => arc(t, L) < LEAD.contactPeople * person || members.some(m => arc(t, m.surfaceDir) < LEAD.contactPeople * person));
  if (contact && members.length >= 2) return bound(to);

  return step('approach', { to, staged, since: prev?.kind === 'approach' ? prev.since : tick });
}

/**
 * The side to come in from: a staging point just past the hold zone, on the bearing round the objective that scores best —
 * out of each known enemy's sight, not on the enemy's side of the objective (that is coming in through them), by cover,
 * high, and not far round. Null when straight in (the bearing toward the leader) is as good, or the leader is already close.
 */
function approachSide(a: LeadArgs, O: Vec3, L: Vec3, zone: number, person: number, arc: (x: Vec3, y: Vec3) => number): Vec3 | null {
  const { state, ps } = a;
  const planet = state.planets[a.objective.planetIndex];
  const R = planet.radius;
  const ring = zone + LEAD.stagePeople * person;
  if (arc(L, O) <= ring + person) return null;
  const tangentTo = (d: Vec3): Vec3 | null => { const t = vecSub(vecNormalize(d), vecScale(O, vecDot(vecNormalize(d), O))); return vecLength(t) < 1e-12 ? null : vecNormalize(t); };
  const toLeader = tangentTo(L);
  if (!toLeader) return null;
  const across = vecNormalize(vecCross(O, toLeader));
  const theta = ring / R;
  const groundO = personSurfaceRadius(planet, O, ps);
  const eyes = a.threats.slice(0, 3).map(t => ({ eye: personPointOn(planet, t, ps, ps.losEyeHeight), side: tangentTo(t) }));
  const score = (S: Vec3): number => {
    let s = 0;
    const body = personPointOn(planet, S, ps, ps.hitboxCenter);
    const out = tangentTo(S)!;
    for (const e of eyes) {
      if (personLineOfSight(e.eye, body, state) && !segmentCovered(e.eye, body, state, undefined, ps.coverSampleStep)) s -= 2;   // in its sight
      if (e.side) { const c = vecDot(out, e.side); if (c > 0.5) s -= 2 * c; }   // on the enemy's side of the objective
    }
    let cover = 0;
    for (const o of a.obstacles) if (arc(o.dir, S) < 2 * person + o.radius) cover++;
    s += 0.5 * Math.min(2, cover);
    s += 0.3 * (personSurfaceRadius(planet, S, ps) - groundO) / person;
    s -= 0.1 * (arc(L, S) + arc(S, O) - arc(L, O)) / person;
    return s;
  };
  const at = (bearing: number): Vec3 => {
    const t = vecAdd(vecScale(toLeader, dcos(bearing)), vecScale(across, dsin(bearing)));
    return vecNormalize(vecAdd(vecScale(O, dcos(theta)), vecScale(t, dsin(theta))));
  };
  const straight = score(at(0));
  let best: Vec3 | null = null, bestScore = straight + LEAD.sideMargin;
  for (let k = 1; k < LEAD.bearings; k++) {
    const S = at((2 * Math.PI * k) / LEAD.bearings);
    const s = score(S);
    if (s > bestScore) { best = S; bestScore = s; }
  }
  return best;
}

/**
 * 66.7: the leader's call after a contact drill. Withdraw as the units' own judgement says (actionJobs: the scores every
 * bot's job is chosen by, with the default weights, for the squad — its mean health, the enemies it knows near against its
 * members, the objective's pull, `takeDist` in person lengths as the jobs read them: retreat). Else, `ordered` (a move or a
 * take: the coordinator's ruling), bound on — unless outnumbered (strictly more enemies known near the contact than its
 * living members: three bound on against one, two or three, and hold against four or more — mass at a held point is the
 * commander's to bring, not the drill's) or its mean health under half, when it holds the drill's posts and looks again.
 * Unordered, the jobs' call: engage holds, take (or anything else) bounds on.
 */
export function drillCall(members: readonly PlayerState[], enemiesNear: number, takeDist: number, ordered = false): 'hold' | 'bound' | 'withdraw' {
  const health = members.reduce((s, m) => s + Math.max(0, Math.min(1, m.health / 100)), 0) / Math.max(1, members.length);
  const job = chooseJob({ hasTarget: true, health, threats: enemiesNear, alliesNear: members.length - 1, takeDist, holdDist: null, lastKnownAge: 0, order: 'take', current: null });
  if (job === 'retreat') return 'withdraw';
  if (ordered) return enemiesNear > members.length || health < DRILL_HOLD_HEALTH ? 'hold' : 'bound';
  return job === 'engage' ? 'hold' : 'bound';
}
/** An ordered squad under this mean health holds after its drill rather than bound on. */
const DRILL_HOLD_HEALTH = 0.5;

function centroid(dirs: readonly Vec3[]): Vec3 {
  let s: Vec3 = { x: 0, y: 0, z: 0 };
  for (const d of dirs) s = vecAdd(s, vecNormalize(d));
  return vecNormalize(s);
}
